import { hasMediaAsset } from './mediaRegistry';

export type ContextualMediaScope = 'editorial' | 'property' | 'stand';
export type ContextualMediaSpecies =
  | 'pine'
  | 'spruce'
  | 'birch'
  | 'deciduous'
  | 'mixed'
  | 'other'
  | 'unknown';
export type ContextualMediaDevelopmentClass = 'A0' | 'T1' | 'T2' | 'unknown';
export type ContextualMediaLandClass =
  | 'productive'
  | 'low-productive'
  | 'nonproductive-peatland'
  | 'other'
  | 'unknown';
export type ContextualMediaStructure =
  | 'pine-overstory-spruce-regeneration'
  | 'birch-overstory-spruce-regeneration'
  | 'other'
  | 'unknown';
export type ContextualMediaCondition =
  | 'conifer-mortality'
  | 'broken-trees'
  | 'other'
  | 'unknown';
export type ContextualMediaCause =
  | 'insect-or-parasite'
  | 'pathogen'
  | 'wind'
  | 'snow'
  | 'fire'
  | 'drought'
  | 'machinery'
  | 'other'
  | 'unknown';

export interface ContextualActualMedia {
  identity: string;
  evidenceKind:
    | 'field-image'
    | 'inspection-map'
    | 'orthophoto'
    | 'canopy-raster'
    | 'property-image'
    | 'other';
}

/**
 * Source-resolved semantic request. It deliberately contains no URL, file,
 * crop, layout, style, or renderer data.
 */
export interface ContextualForestMediaRequest {
  scope: ContextualMediaScope;
  species?: ContextualMediaSpecies;
  developmentClass?: ContextualMediaDevelopmentClass;
  landClass?: ContextualMediaLandClass;
  structure?: ContextualMediaStructure;
  condition?: ContextualMediaCondition;
  reportedCause?: ContextualMediaCause;
  basisLabel?: string;
  sourceRevision?: string;
  freshness?: string;
  actualMedia?: ContextualActualMedia;
  allowIllustrativeFallback: boolean;
  stateId?: string;
}

export type ContextualForestMediaResolution =
  | {
      identity: string;
      mode: 'actual';
      evidenceKind: ContextualActualMedia['evidenceKind'];
    }
  | {
      identity: string;
      mode: 'illustrative';
    };

const SPECIES_IDENTITIES: Partial<Record<ContextualMediaSpecies, string>> = {
  pine: 'forest.species.pine.overview',
  spruce: 'forest.species.spruce.overview',
  birch: 'forest.species.birch.overview',
  deciduous: 'forest.species.deciduous.overview',
  mixed: 'forest.species.mixed.overview',
};

const DEVELOPMENT_IDENTITIES: Partial<Record<ContextualMediaDevelopmentClass, string>> = {
  A0: 'forest.development.a0.regeneration',
  T1: 'forest.development.t1.established-seedling',
};

const STRUCTURE_IDENTITIES: Partial<Record<ContextualMediaStructure, string>> = {
  'pine-overstory-spruce-regeneration': 'forest.structure.pine-overstory-spruce-regeneration',
  'birch-overstory-spruce-regeneration': 'forest.structure.birch-overstory-spruce-regeneration',
};

const CONDITION_IDENTITIES: Partial<Record<ContextualMediaCondition, string>> = {
  'conifer-mortality': 'forest.condition.conifer-mortality',
  'broken-trees': 'forest.condition.broken-trees',
};

function registered(identity: string | undefined): string | null {
  return identity && hasMediaAsset(identity) ? identity : null;
}

/** Resolve one governed identity together with renderer-owned provenance. */
// eslint-disable-next-line complexity -- ordered evidence-first resolution is the semantic media policy.
export function resolveContextualForestMediaResult(
  request: ContextualForestMediaRequest,
): ContextualForestMediaResolution | null {
  const actualIdentity = registered(request.actualMedia?.identity);
  if (actualIdentity && request.actualMedia) {
    return {
      identity: actualIdentity,
      mode: 'actual',
      evidenceKind: request.actualMedia.evidenceKind,
    };
  }
  if (!request.allowIllustrativeFallback) return null;

  const conditionIdentity = registered(CONDITION_IDENTITIES[request.condition ?? 'unknown']);
  if (conditionIdentity) return { identity: conditionIdentity, mode: 'illustrative' };

  const structureIdentity = registered(STRUCTURE_IDENTITIES[request.structure ?? 'unknown']);
  if (structureIdentity) return { identity: structureIdentity, mode: 'illustrative' };

  if (request.developmentClass === 'T2' && request.species === 'spruce') {
    const identity = registered('forest.development.t2.spruce.detail');
    return identity ? { identity, mode: 'illustrative' } : null;
  }

  if (request.landClass === 'nonproductive-peatland') {
    const identity = registered('forest.land.nonproductive-peatland');
    return identity ? { identity, mode: 'illustrative' } : null;
  }

  const developmentIdentity = registered(
    DEVELOPMENT_IDENTITIES[request.developmentClass ?? 'unknown'],
  );
  if (developmentIdentity) return { identity: developmentIdentity, mode: 'illustrative' };

  const speciesIdentity = registered(SPECIES_IDENTITIES[request.species ?? 'unknown']);
  return speciesIdentity ? { identity: speciesIdentity, mode: 'illustrative' } : null;
}

/** Return one stable semantic media identity, or null when truth is insufficient. */
export function resolveContextualForestMedia(
  request: ContextualForestMediaRequest,
): string | null {
  return resolveContextualForestMediaResult(request)?.identity ?? null;
}

/**
 * Optional owner-facing condition wording. Cause is named only when it arrives
 * as a separate served field; it never changes the selected condition image.
 */
export function describeContextualForestCondition(
  request: ContextualForestMediaRequest,
): string | null {
  if (request.condition !== 'conifer-mortality' && request.condition !== 'broken-trees') {
    return null;
  }
  const observed = request.condition === 'conifer-mortality'
    ? 'Observed conifer mortality or crown decline'
    : 'Observed broken or fallen trees';
  const cause = request.reportedCause;
  if (!cause || cause === 'unknown') return `${observed}; cause not reported.`;
  return `${observed}; reported cause: ${cause.replace(/-/g, ' ')}.`;
}

export function resolveGovernedMediaIdentity({
  assetKey,
  mediaContext,
}: {
  assetKey?: string;
  mediaContext?: ContextualForestMediaRequest;
}): string | null {
  if (assetKey && mediaContext) {
    throw new Error('Pass either a governed asset key or mediaContext, never both.');
  }
  return mediaContext
    ? resolveContextualForestMedia(mediaContext)
    : registered(assetKey);
}
